import React, { useState } from 'react';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { Menu, MenuItem, MenuProps } from '@mui/material';

import { DocumentTypes, Pledge } from '@/types';
import { getDocument } from '@/services';
import { PreviewDocument } from '@/components';
import { Langs, LANGS } from '@/utils/langs';
import { NomenclatureGroups } from '@/utils/enums';
import { catchErrorForBlob } from '@/utils/helpers';

export interface GeneratePledgeDocumentProps extends MenuProps {
  pledge: Pledge | null;
}

const GeneratePledgeDocument: React.FC<GeneratePledgeDocumentProps> = ({ pledge, ...props }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [lan, setLang] = useState('');
  const [anchorEl, setAnchorEl] = useState<HTMLLIElement | null>(null);
  const [previewDocumentOpened, setOpenedPreviewDocument] = useState(false);
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);

  async function handleSelectDocument(type: DocumentTypes) {
    try {
      setLoading(true);

      const document = (await getDocument({
        params: { pledge_id: pledge?.id, lan, type },
        responseType: 'blob',
      })) as Blob;

      setOpenedPreviewDocument(true);
      setDocumentUrl(URL.createObjectURL(document));
    } catch (error) {
      enqueueSnackbar((await catchErrorForBlob(error)).error.data, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }

  function handleSelectLang(value: Langs) {
    return (event: React.MouseEvent<HTMLLIElement>) => {
      setLang(value);
      setAnchorEl(event.currentTarget);
    };
  }

  function handleCloseDialog() {
    if (documentUrl) URL.revokeObjectURL(documentUrl);
    setDocumentUrl(null);
    setOpenedPreviewDocument(false);
  }

  return (
    <>
      <Menu {...props}>
        {LANGS.map(({ label, value }) => (
          <MenuItem key={value} value={value} disabled={loading} onClick={handleSelectLang(value)}>
            {label}
          </MenuItem>
        ))}
      </Menu>

      <Menu open={anchorEl !== null} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}>
        <MenuItem
          value={DocumentTypes.WHOLE_DOCUMENT}
          disabled={loading}
          divider
          onClick={() => handleSelectDocument(DocumentTypes.WHOLE_DOCUMENT)}
        >
          Весь документ
        </MenuItem>

        {moment().isSame(pledge?.created_at, 'day') && (
          <MenuItem
            value={DocumentTypes.STATEMENT}
            disabled={loading}
            onClick={() => handleSelectDocument(DocumentTypes.STATEMENT)}
          >
            Заявление
          </MenuItem>
        )}

        <MenuItem
          value={DocumentTypes.TITLE}
          disabled={loading}
          onClick={() => handleSelectDocument(DocumentTypes.TITLE)}
        >
          Титульный лист
        </MenuItem>

        {!pledge?.first_pledge_id && moment().isSame(pledge?.created_at, 'day') && (
          <MenuItem
            value={DocumentTypes.PACKING_SLIP}
            disabled={loading}
            onClick={() => handleSelectDocument(DocumentTypes.PACKING_SLIP)}
          >
            Упаковочный талон
          </MenuItem>
        )}

        {pledge?.nomenclature_group?.name === NomenclatureGroups.CARS && (
          <MenuItem
            value={DocumentTypes.SCHEDULE}
            disabled={loading}
            onClick={() => handleSelectDocument(DocumentTypes.SCHEDULE)}
          >
            График
          </MenuItem>
        )}
      </Menu>

      <PreviewDocument onClose={handleCloseDialog} documentUrl={documentUrl} open={previewDocumentOpened} />
    </>
  );
};

export default GeneratePledgeDocument;
